import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { TooltipProvider } from '@/common/ui/tooltip';
import {
  getLeadershipTeams,
  getCurrentOrganizationId,
  isCurrentMemberTeamLeader,
  isCurrentMemberOrgAdmin,
  canCurrentMemberAccessCultureHub,
  getMemberId,
  getSelectedAssessmentPeriod,
} from 'common/selectors/auth';
import featureSlice from 'common/slices/featureSlice';
import { useGetAssessmentPeriodsByScopeQuery } from 'areas/assessment-schedule/api/assessmentPeriodApi';
import userContextSlice from 'common/slices/userContextSlice';
import { getSelectedAssessmentPeriodId } from 'common/selectors/auth';
import LandingPage from 'common/layouts/AuthLayout/LandingPage';
import { useCultureHubTopBarControls } from './shared/useCultureHubTopBarControls';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip as ChartTooltip,
} from 'chart.js';
import { CultureHubContent } from './CultureHubContent';
import { DashboardView, dashboardViewOptions } from './analytics/AnalyticsViewSelector';
import AssessmentPeriodListWithStatus from './shared/period-selector/AssessmentPeriodListWithStatus';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/common/ui/select';
import styled from 'styled-components';
import {
  setDashboardView,
  getDashboardView,
} from '../slices';
import { SemanticICONS } from 'semantic-ui-react';
import { ASSESSMENT_PERIOD_CLOSED } from '@/areas/assessment-schedule/constants';
import ToggleControl from '@/common/components/ToggleChartControl';
import DashboardTeam from 'areas/dashboard/components/DashboardTeam';
import { PageContent, PageSection } from '@/common/styles/page';

const PageContentNoPadding = styled.div`
  font-family: 'Poppins';
  font-style: normal;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  box-sizing: border-box;
  background-color: transparent;
`;

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  RadialLinearScale,
  ChartTooltip
);

export default function CultureIntelligenceHub({ initialView, leader_id }: { initialView?: 'main' | 'team', leader_id?: number | null } = {}) {
  const reduxDispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tab: urlTab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const organizationId = useSelector(getCurrentOrganizationId);
  const isTeamLeader = useSelector(isCurrentMemberTeamLeader);
  const isOrgAdmin = useSelector(isCurrentMemberOrgAdmin);
  const leadershipTeams = useSelector(getLeadershipTeams);
  const selectedAssessmentPeriod = useSelector(getSelectedAssessmentPeriod);

  // Current member id used to filter which teams they lead
  const currentMemberId = useSelector(getMemberId);
  // Persisted selected team id from user context
  const selectedTeamIdGlobal = useSelector(userContextSlice.selectors.getSelectedTeamId as any) as number | undefined;

  const canAccess = useSelector(canCurrentMemberAccessCultureHub);

  const features = useSelector(featureSlice.selectors.getFeatures);
  const isBioTabEnabled = features.some(f => f.name === 'Culture Hub Bio' && f.enabled);

  const dashboardView = useSelector(getDashboardView);
  const selectedAssessmentPeriodId = useSelector(getSelectedAssessmentPeriodId) as number | null;

  const defaultTab = 'analytics';
  const activeTab = (urlTab as 'bio' | 'analytics' | 'pillars' | 'interventions' | undefined) || defaultTab;

  const leadershipTeamsForSelectedPeriod = useMemo(() => {
    if (!selectedAssessmentPeriod || !Array.isArray(leadershipTeams)) {
      return [];
    }

    return leadershipTeams.filter(team =>
      Array.isArray(team.assessments) && team.assessments.includes(selectedAssessmentPeriod.id)
    );
  }, [leadershipTeams, selectedAssessmentPeriod]);

  const managesTeams = isTeamLeader || (isOrgAdmin && leadershipTeamsForSelectedPeriod.length > 0);

  const [view, setView] = useState<'main' | 'team'>(
    initialView ?? 'main'
  );

  // Ensure the toggle only applies to the analytics tab. If the user navigates to a
  // non-analytics tab (bio/pillars/interventions) force the view to 'main' so the route
  // navigation renders the expected tab component.
  useEffect(() => {
    if (activeTab !== 'analytics' && view !== 'main') {
      setView('main');
    }
  }, [activeTab, view]);




  const urlDashboardView = searchParams.get('dashboardView') as DashboardView | null;

  // Initialize Redux state from URL params on mount -- do NOT add parameters
  useEffect(() => {
    const initialView: DashboardView = urlDashboardView && dashboardViewOptions.some(opt => opt.value === urlDashboardView)
      ? urlDashboardView
      : 'dashboard';
    // @ts-ignore
    reduxDispatch(setDashboardView(initialView));
  }, []); // Only on mount


  // Redirect to default tab if no tab specified or bio tab is disabled
  useEffect(() => {
    if (!urlTab || (activeTab === 'bio' && !isBioTabEnabled)) {
      navigate(`/app/culture-hub/${defaultTab}?${searchParams.toString()}`, { replace: true });
    }
  }, [urlTab, activeTab, isBioTabEnabled, defaultTab, navigate, searchParams]);

  const {
    data: assessmentPeriods,
    isLoading: isLoadingPeriods,
    error: assessmentPeriodsError,
    isError: isAssessmentPeriodsError
  } = useGetAssessmentPeriodsByScopeQuery(
    undefined,
    { skip: !organizationId }
  );

  const selectedLeadershipTeam = leadershipTeams?.find(
    team => team.id === selectedTeamIdGlobal
  );

  const isTeamLeaderWithNoTeams = useMemo(() => (
    isTeamLeader && Array.isArray(leadershipTeams) && leadershipTeams.length === 0
  ), [isTeamLeader, leadershipTeams]);

  const shouldShowNoTeamsPage = useMemo(() => (
    isTeamLeaderWithNoTeams ||
    (!isLoadingPeriods &&
      !!selectedAssessmentPeriod &&
      leadershipTeamsForSelectedPeriod.length === 0 &&
      (isOrgAdmin || isTeamLeader))
  ), [isTeamLeaderWithNoTeams, isLoadingPeriods, selectedAssessmentPeriod, leadershipTeamsForSelectedPeriod.length, isOrgAdmin, isTeamLeader]);

  const filteredAssessmentPeriods = useMemo(() => {
    if (!selectedTeamIdGlobal || !assessmentPeriods?.length) {
      return assessmentPeriods;
    }
    if (!selectedLeadershipTeam?.assessments?.length) {
      return [];
    }
    return assessmentPeriods.filter(period =>
      selectedLeadershipTeam.assessments.includes(period.id)
    );
  }, [selectedTeamIdGlobal, selectedLeadershipTeam?.assessments, assessmentPeriods]);

  useEffect(() => {
    if (!selectedTeamIdGlobal || !filteredAssessmentPeriods?.length) {
      return;
    }

    const currentId = selectedAssessmentPeriod?.id;
    if (!currentId || !filteredAssessmentPeriods.some(period => period.id === currentId)) {
      reduxDispatch(
        userContextSlice.actions.setSelectedAssessmentPeriod({
          selectedAssessmentPeriod: filteredAssessmentPeriods[0],
          assessmentPeriods: filteredAssessmentPeriods,
        })
      );
    }
  }, [selectedTeamIdGlobal, filteredAssessmentPeriods, selectedAssessmentPeriod?.id, reduxDispatch]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    if (dashboardView !== 'dashboard') {
      params.set('dashboardView', dashboardView);
    } else {
      params.delete('dashboardView');
    }

    setSearchParams(params, { replace: true });
  }, [
    dashboardView,
    searchParams,
    setSearchParams,
  ]);

  // Memoize leader teams and topbar JSX to avoid unnecessary immediate re-rendering
  const leadershipTeamsList = leadershipTeams || []; 

  // Default selection side-effect (kept separate from rendering logic)
  useEffect(() => {
    if (
      managesTeams &&
      (selectedTeamIdGlobal === undefined || selectedTeamIdGlobal === null) &&
      leadershipTeamsForSelectedPeriod.length > 0
    ) {
      const defaultTeam = leadershipTeamsForSelectedPeriod[0];
      if (defaultTeam?.id != null) {
        reduxDispatch(userContextSlice.actions.setSelectedTeamId(Number(defaultTeam.id)));
      }
    }
  }, [managesTeams, leadershipTeamsForSelectedPeriod, selectedTeamIdGlobal, reduxDispatch]);

  const showSelector = managesTeams && leadershipTeamsForSelectedPeriod.length > 1;

  const toggleButtons = useMemo(
    () => [
      {
        type: 'main',
        icon: 'chart bar' as SemanticICONS,
        disabled: selectedAssessmentPeriod?.status !== ASSESSMENT_PERIOD_CLOSED,
      },
      {
        type: 'team',
        icon: 'users' as SemanticICONS,
        disabled: !selectedAssessmentPeriod,
      },
    ],
    [selectedAssessmentPeriod?.status, selectedAssessmentPeriod]
  );

  const topBarExtraControls = useMemo(
    () =>
      activeTab === 'analytics' ? (
        <ToggleControl
          buttonLabels={toggleButtons}
          activeToggle={view}
          setChartType={val => setView(val as 'main' | 'team')}
          size="mini"
        />
      ) : null,
    [activeTab, toggleButtons, view]
  );

  const handleTopBarTeamChange = useCallback(
    (id: number | null) => {
      reduxDispatch(userContextSlice.actions.setSelectedTeamId(id));
    },
    [reduxDispatch]
  );

  const handleTopBarAssessmentPeriodChange = useCallback(
    (value: string | number | undefined, period?: any) => {
      const id = typeof value === 'string' ? parseInt(value, 10) : value;
      const selected = period ?? assessmentPeriods?.find(p => p.id === id);
      if (selected) {
        reduxDispatch(
          userContextSlice.actions.setSelectedAssessmentPeriod({
            selectedAssessmentPeriod: selected,
            assessmentPeriods: assessmentPeriods || [],
          })
        );
      }
    },
    [reduxDispatch, assessmentPeriods]
  );

  useCultureHubTopBarControls({
    showSelector,
    selectedTeamIdGlobal,
    leadershipTeams: leadershipTeamsList,
    selectedAssessmentPeriodId,
    filteredAssessmentPeriods,
    isLoadingPeriods,
    onTeamChange: handleTopBarTeamChange,
    onAssessmentPeriodChange: handleTopBarAssessmentPeriodChange,
    extraControls: topBarExtraControls,
  });

  if (isAssessmentPeriodsError) {
    return (
      <PageContentNoPadding data-no-container-padding="true">
        <div
          style={{ backgroundColor: 'transparent', minHeight: 'calc(100vh - 100px)', padding: '15px' }}
        >
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>Error Loading Assessment Periods</h2>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>
              {assessmentPeriodsError && 'message' in assessmentPeriodsError
                ? String(assessmentPeriodsError.message)
                : 'Unable to load assessment periods. Please try refreshing the page.'}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '8px 16px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Refresh Page
            </button>
          </div>
        </div>
      </PageContentNoPadding>
    );
  }

  if (!isLoadingPeriods && (!assessmentPeriods || assessmentPeriods.length === 0)) {
    return (
      <PageContentNoPadding data-no-container-padding="true">
        <div
          style={{ backgroundColor: 'transparent', minHeight: 'calc(100vh - 100px)', padding: '15px' }}
        >
          <LandingPage
            title="Welcome to nuLogic!"
            subtitle="Schedule an assessment to collect organization sentiment data and unlock culture insights..."
            buttonLabel=""
          />
        </div>
      </PageContentNoPadding>
    );
  }

  const hasNoTeamsAssigned = shouldShowNoTeamsPage;

  const noTeamsPageTitle = isTeamLeader
    ? 'No Team Leader Data Available'
    : 'No Teams Assigned';

  const noTeamsPageSubtitle = isTeamLeader
    ? 'No team leader data is available for this assessment period.'
    : "You don't have any teams assigned for this assessment period.";

  const noTeamsPageDetail = isTeamLeader
    ? "If you believe this is an error, please contact your organization's nuLogic administrator."
    : "Please contact your organization's nuLogic administrator to assign you to a team.";

  const renderNoTeamsMessage = () => (
    <div style={{
      textAlign: 'center',
      padding: '20px',
      minHeight: '100%',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      backgroundColor: 'transparent'
    }}>
      <h2 style={{ color: '#161616', marginBottom: '16px' }}>{noTeamsPageTitle}</h2>
      <p style={{ color: '#6b7280', marginBottom: '12px' }}>
        {noTeamsPageSubtitle}
      </p>
      <p style={{ color: '#6b7280', marginBottom: '24px' }}>
        {noTeamsPageDetail}
      </p>
      <div style={{
        backgroundColor: '#fff',
        padding: '20px',
        borderRadius: '8px',
        maxWidth: '500px',
        margin: '0 auto',
        textAlign: 'left'
      }}>
      </div>
    </div>
  );

  return (
    <TooltipProvider>
      <PageContentNoPadding data-no-container-padding="true">
        <div
          style={{
            backgroundColor: 'transparent',
            minHeight: 'calc(100vh - 50px)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{
            padding: '0',
            flex: 1,
            backgroundColor: 'transparent',
            minHeight: 'calc(100vh - 150px)',
            display: 'flex',
            flexDirection: 'column',
          }}>
            {hasNoTeamsAssigned ? (
              renderNoTeamsMessage()
            ) : view === 'main' ? (
              <CultureHubContent activeTab={activeTab} />
            ) : (
              <PageContent>
                <PageSection padtop padbottom padleft padright>
                  <DashboardTeam leaderId={leader_id} />
                </PageSection>
              </PageContent>
            )}
          </div>
        </div>
      </PageContentNoPadding>
    </TooltipProvider>
  );
}
